<template>
  <aside class="relative rounded-[32px] bg-light px-6 py-6">
    <span class="absolute hidden lg:inline-flex left-4 -top-6  h-12 w-12 items-center justify-center rounded-full bg-primary leading-none text-light">
      <TicketIcon />
    </span>

    <h2 class="mt-4 flex items-center gap-3 text-[18px] font-medium lg:items-start lg:gap-0 lg:text-2xl">
      <TicketIcon class="inline-flex lg:hidden" />
      Booking Summary
    </h2>

    <div v-if="quoteEnabled && quoteLoading" class="mt-4 text-sm text-dark/60">
      Updating booking summary...
    </div>
    <div v-else-if="quoteEnabled && quoteFailed" class="mt-4 text-sm text-dark/60">
      Couldn't update booking summary.
      <button type="button" class="underline underline-offset-4" @click="buildQuote">Try again</button>
    </div>

    <div v-if="summaryItems.length" class="mt-6 space-y-5 border-t border-dashed border-primary pt-5">
      <div
        v-for="item in summaryItems"
        :key="`${item.id}-summary`"
        class="space-y-3 border-b border-dashed border-primary pb-5 last:border-b-0 last:pb-0"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <div class="flex items-start gap-2">
              <a
                v-if="showEditLinks"
                :href="editUrl"
                class="text-[17px] font-medium underline underline-offset-4 lg:text-[18px] lg:no-underline"
              >
                {{ item.title }}
              </a>
              <div v-else class="text-[18px] font-medium">{{ item.title }}</div>
              <AlertErrorIcon
                v-if="isTimeConflictItem(item)"
                class="mt-0.5 h-5 w-5 shrink-0 text-alert-error"
              />
            </div>
            <div class="mt-1 text-[15px] text-dark/60">{{ formatLongDate(item.time) }}, {{ formatTime(item.time) }}</div>
          </div>

          <a
            v-if="showEditLinks"
            :href="editUrl"
            class="shrink-0 text-[14px] font-semibold underline underline-offset-4 lg:hidden"
          >
            Edit Cart
          </a>
        </div>

        <div
          v-for="ticket in item.tickets"
          :key="`${item.id}-${ticket.key}`"
          class="flex items-center justify-between gap-4 text-[15px]"
        >
          <div>{{ ticket.count }}x {{ ticket.label }}</div>
          <div>{{ formatCurrency(ticket.total) }}</div>
        </div>
      </div>
    </div>

    <div v-if="upsellDiscounts.length" class="mt-6 space-y-3 border-t border-dashed border-primary pt-5">
      <div
        v-for="(discount, index) in upsellDiscounts"
        :key="`${discount.id ?? index}-upsell-discount`"
        class="flex items-start justify-between gap-4 text-[15px]"
      >
        <div>{{ discount.title ?? 'Total discount' }}</div>
        <div class="font-medium">-{{ formatCurrency(discount.discount_value) }}</div>
      </div>
    </div>

    <div v-if="voucherDiscounts.length" class="mt-6 space-y-3 border-t border-dashed border-primary pt-5">
      <div
        v-for="(discount, index) in voucherDiscounts"
        :key="`${discount.id ?? index}-voucher-discount`"
        class="flex items-start justify-between gap-4 text-[15px]"
      >
        <div>
          <div>{{ discount.title }}</div>
          <div v-if="discount.description" class="mt-1 text-sm text-dark/60">{{ discount.description }}</div>
        </div>
        <div class="shrink-0 font-medium">-{{ formatCurrency(discount.discount_value) }}</div>
      </div>
    </div>

    <div class="mt-6 flex items-start justify-between gap-4 border-t border-dashed border-primary pt-5">
      <div>
        <div class="text-[18px] font-medium">{{ totalLabel }}</div>
      </div>
      <div class="text-right">
        <div class="text-xl font-medium">{{ formatCurrency(summaryTotal) }}</div>
        <div class="mt-1 text-sm text-dark/60">AUD (inc. GST)</div>
      </div>
    </div>

    <div v-if="warnings.length" class="mt-6 space-y-3">
      <alert-error
          :link="from === 'checkout' ? editUrl : null"
          v-for="(warning, index) in warnings"
          :key="`${index}-${warning}`">
        <div class="text-base">{{ warning }}</div>
      </alert-error>
    </div>

    <a
      v-if="showCheckoutButton && cartItems.length"
      :href="canCheckout ? checkoutUrl : '#'"
      :class="{ 'opacity-50': !canCheckout }"
      class="mt-6 inline-flex w-full items-center justify-center rounded-full bg-primary px-8 py-4 text-[16px] font-semibold text-dark"
    >
      {{ checkoutLabel }}
    </a>

    <slot />
  </aside>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useCheckoutCartStore } from '../store'
import { buildParticipantSummary, findTicketOption, getExtraRows } from '../helpers/cart'
import { bookingItemConflictKey, bookingItemsTooCloseKeys } from '../helpers'
import { formatCurrency, formatLongDate, formatTime, normaliseParticipantKey } from '../helpers/format'
import { calculateLocalUpsellDiscounts } from '../helpers/upsells'
import { reportLoadError } from '../../helpers/network'
import TicketIcon from "@/checkout/components/TicketIcon.vue";
import AlertError from "@/checkout/components/AlertError.vue";
import AlertErrorIcon from './AlertErrorIcon.vue'

const props = defineProps({
  from: {
    type: String,
  },
  checkoutLabel: {
    type: String,
    default: 'Checkout Now',
  },
  checkoutUrl: {
    type: String,
    default: '/checkout/',
  },
  editUrl: {
    type: String,
    default: '/cart/',
  },
  items: {
    type: Array,
    default: () => [],
  },
  quoteEnabled: {
    type: Boolean,
    default: false,
  },
  showCheckoutButton: {
    type: Boolean,
    default: false,
  },
  showEditLinks: {
    type: Boolean,
    default: false,
  },
  totalLabel: {
    type: String,
    default: 'Total',
  },
  vouchers: {
    type: [Array, Boolean],
    default: false,
  },
  warnings: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['errors', 'loading'])

const QUOTE_FAILED_MESSAGE = "We couldn't confirm your total with the booking system. Please try again."

const store = useCheckoutCartStore()
const { cartItems, quote, selectedUpsellOffers, totalPeople } = storeToRefs(store)
const quoteLoading = ref(false)
const quoteErrors = ref([])
const quoteFailed = computed(() => quoteErrors.value.includes(QUOTE_FAILED_MESSAGE))
let quoteTimer = null
let quoteController = null

const hasTime = computed(() => props.items.length > 0 && props.items.every((item) => Boolean(item.time)))
const hasParticipants = computed(() => props.items.length > 0 && props.items.every((item) => (
  Object.values(item.participants ?? {}).reduce((total, value) => total + Number(value || 0), 0) > 0
)))
const quoteItemsAvailable = computed(() => props.quoteEnabled && Array.isArray(quote.value?.items) && quote.value.items.length > 0)
const localUpsellDiscounts = computed(() => calculateLocalUpsellDiscounts(selectedUpsellOffers.value, cartItems.value))

const localUpsellDiscountTotal = computed(() => localUpsellDiscounts.value.reduce((total, discount) => {
  return total + Number(discount.discount_value ?? 0)
}, 0))

const upsellDiscounts = computed(() => (
  props.quoteEnabled && Array.isArray(quote.value?.upsell_discounts)
    ? quote.value.upsell_discounts
    : localUpsellDiscounts.value
))

const parseDiscountNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

const activeVoucherDetails = computed(() => {
  if (Array.isArray(props.vouchers) && props.vouchers.length) {
    return props.vouchers
  }

  if (props.quoteEnabled && Array.isArray(quote.value?.vouchers) && quote.value.vouchers.length) {
    return quote.value.vouchers
  }

  return []
})

const quoteVoucherDiscountTotal = computed(() => (
  props.quoteEnabled
    ? parseDiscountNumber(quote.value?.totalPaid) ?? 0
    : 0
))

const voucherCode = (voucher, index) => {
  if (typeof voucher === 'string') {
    return voucher
  }

  return voucher?.code
    ?? voucher?.label
    ?? voucher?.coupon
    ?? voucher?.voucher
    ?? voucher?.name
    ?? `Voucher ${index + 1}`
}

const voucherDetailLabel = (voucher) => {
  if (!voucher || typeof voucher === 'string') {
    return ''
  }

  const value = parseDiscountNumber(voucher.value)

  if (voucher.valueType === 'PERCENT' && value !== null) {
    return `${value}% off`
  }

  if (voucher.valueType === 'VALUE' && value !== null) {
    return `${formatCurrency(value)} off`
  }

  if (voucher.valueType === 'PRODUCT') {
    return 'Product voucher'
  }

  return voucher.description ?? voucher.type ?? ''
}

const voucherAmount = (voucher) => {
  if (!voucher || typeof voucher === 'string') {
    return {
      amount: 0,
      exact: false,
    }
  }

  const explicitAmount = parseDiscountNumber(
    voucher.discount_value
    ?? voucher.discountValue
    ?? voucher.discount
    ?? voucher.amount
    ?? voucher.totalPaid
  )

  if (explicitAmount !== null) {
    return {
      amount: Math.abs(explicitAmount),
      exact: true,
    }
  }

  const value = parseDiscountNumber(voucher.value)

  if (voucher.valueType === 'VALUE' && value !== null) {
    return {
      amount: Math.abs(value),
      exact: true,
    }
  }

  if (voucher.valueType === 'PERCENT' && value !== null) {
    return {
      amount: Math.max(0, store.cartSubtotal * (value / 100)),
      exact: false,
    }
  }

  return {
    amount: 0,
    exact: false,
  }
}

const voucherDiscounts = computed(() => {
  const vouchers = activeVoucherDetails.value
  const quoteDiscount = quoteVoucherDiscountTotal.value

  if (!vouchers.length) {
    return quoteDiscount > 0
      ? [{
        id: 'voucher-quote-discount',
        title: 'Voucher/Coupon',
        description: '',
        discount_value: quoteDiscount,
      }]
      : []
  }

  const perVoucherRows = vouchers.map((voucher, index) => {
    const code = voucherCode(voucher, index)
    const detail = voucherDetailLabel(voucher)
    const discount = voucherAmount(voucher)

    return {
      id: `${code}-${index}`,
      title: code,
      description: detail,
      discount_value: discount.amount,
      exact: discount.exact,
    }
  })

  if (perVoucherRows.length === 1 && quoteDiscount > 0) {
    return [{
      ...perVoucherRows[0],
      discount_value: quoteDiscount,
    }]
  }

  const perVoucherTotal = perVoucherRows.reduce((total, voucher) => total + Number(voucher.discount_value ?? 0), 0)
  const hasOnlyExactVoucherAmounts = perVoucherRows.every((voucher) => voucher.exact)

  if (perVoucherTotal > 0 && (!quoteDiscount || hasOnlyExactVoucherAmounts)) {
    return perVoucherRows.filter((voucher) => Number(voucher.discount_value ?? 0) > 0)
  }

  if (quoteDiscount <= 0) {
    return []
  }

  return [{
    id: 'voucher-quote-discount',
    title: 'Voucher/Coupon',
    description: perVoucherRows.map((voucher) => (
      voucher.description ? `${voucher.title} - ${voucher.description}` : voucher.title
    )).join(', '),
    discount_value: quoteDiscount,
  }]
})

const canCheckout = computed(() => !props.warnings.length)
const localVoucherDiscountTotal = computed(() => (
  props.quoteEnabled
    ? 0
    : voucherDiscounts.value.reduce((total, discount) => total + Number(discount.discount_value ?? 0), 0)
))

const summaryTotal = computed(() => {
  if (props.quoteEnabled && quote.value && Object.prototype.hasOwnProperty.call(quote.value, 'totalDue')) {
    return Number(quote.value.totalDue ?? 0)
  }

  return Math.max(0, store.cartSubtotal - localUpsellDiscountTotal.value - localVoucherDiscountTotal.value)
})

// Drop any pending or in-flight quote so an older answer can't overwrite a newer one.
const cancelQuote = () => {
  if (quoteTimer) {
    clearTimeout(quoteTimer)
    quoteTimer = null
  }

  quoteController?.abort()
  quoteController = null
}

const parseQuoteError = (error) => {
  const messages = []

  if (error?.data?.requestStatus?.error?.errorMessage) {
    const message = error.data.requestStatus.error.errorMessage
    messages.push(
      message.includes('is not valid')
        ? `Your coupon/voucher ${message.replace(' is not valid', '')} isn't valid for the current order`
        : message
    )
  }

  if (error?.data?.errors?.errorMessage) {
    messages.push(error.data.errors.errorMessage)
  }

  return messages
}

const checkQuote = async () => {
  const controller = new AbortController()
  quoteController = controller
  quoteErrors.value = []
  quoteLoading.value = true

  let failure = null

  try {
    await store.quoteBooking(controller.signal)
  } catch (error) {
    failure = error
  }

  // A newer quote replaced this one while it was in flight.
  if (controller.signal.aborted) {
    return
  }

  if (failure) {
    // No message means the booking API didn't answer, failed or rejected the quote without saying why
    // (a Rezdy timeout comes back as a bare 500), or the quote broke before it was sent.
    const messages = parseQuoteError(failure)
    quoteErrors.value = messages.length > 0 ? messages : [QUOTE_FAILED_MESSAGE]

    // HTTP errors arrive as the response object, and the booking API reports those itself.
    if (failure instanceof Error) {
      reportLoadError(failure)
    }
  }

  quoteLoading.value = false
  emit('errors', quoteErrors.value)
  emit('loading', false)
}

const buildQuote = () => {
  if (!props.quoteEnabled) {
    return
  }

  if (Number(totalPeople.value ?? 0) < 1 || !hasTime.value || !hasParticipants.value) {
    cancelQuote()
    quoteErrors.value = []
    quoteLoading.value = false
    emit('errors', [])
    emit('loading', false)
    return
  }

  cancelQuote()
  quoteErrors.value = []
  quoteLoading.value = true
  emit('errors', [])
  emit('loading', true)
  quoteTimer = setTimeout(checkQuote, 1000)
}

const attractionTitle = (item) => item.attractionTitle || store.getAttraction(item.attraction)?.retail_title || 'Experience'

const attractionFromProductCode = (productCode) => {
  return store.attractions.length > 0 ? store.attractions.find(a => a.rezdy_retail_product_code === productCode) : null;
}

const chosenTickets = (item) => buildParticipantSummary(item.participants).map((summary) => {
  const [countText, ...labelParts] = summary.split(' ')
  const count = Number(countText)
  const label = labelParts.join(' ')
  const option = findTicketOption(item, normaliseParticipantKey(label))

  return {
    key: label,
    label,
    count,
    total: count * Number(option?.price ?? 0),
  }
})

const quoteItemTickets = (item) => {
  const quantityTickets = (item.quantities ?? []).map((quantity, index) => ({
    key: `${quantity.optionLabel ?? 'ticket'}-${index}`,
    label: quantity.optionLabel ?? 'Ticket',
    count: Number(quantity.value ?? 0),
    total: Number(quantity.optionPrice ?? 0) * Number(quantity.value ?? 0),
  }))

  const extraTickets = (item.extras ?? []).map((extra, index) => ({
    key: `extra-${extra.name ?? index}`,
    label: extra.name ?? 'Extra',
    count: Number(extra.quantity ?? 0),
    total: Number(extra.price ?? 0) * Number(extra.quantity ?? 0),
  }))

  return [...quantityTickets, ...extraTickets].filter((ticket) => ticket.count > 0)
}

const quoteSummaryItems = computed(() => (quote.value?.items ?? []).map((item, index) => {
  const attraction = attractionFromProductCode(item.productCode)

  return {
    id: item.id ?? `${item.productCode ?? 'quote'}-${item.startTimeLocal ?? index}`,
    conflictKey: bookingItemConflictKey(item, index),
    title: attraction?.retail_title ?? item.productName ?? 'Experience',
    time: item.startTimeLocal,
    tickets: quoteItemTickets(item),
  }
}))

const cartSummaryItems = computed(() => cartItems.value.map((item) => ({
  id: item.id,
  conflictKey: bookingItemConflictKey(item),
  title: attractionTitle(item),
  time: item.time,
  tickets: [...chosenTickets(item), ...getExtraRows(item)],
})))

const summaryItems = computed(() => (quoteItemsAvailable.value ? quoteSummaryItems.value : cartSummaryItems.value))
const timeConflictSourceItems = computed(() => (quoteItemsAvailable.value ? quote.value?.items ?? [] : cartItems.value))
const timeConflictItemKeys = computed(() => (
  new Set(bookingItemsTooCloseKeys(timeConflictSourceItems.value))
))
const isTimeConflictItem = (item) => timeConflictItemKeys.value.has(item.conflictKey)

watch(() => props.items, buildQuote, { deep: true, immediate: true })
watch(() => props.vouchers, buildQuote, { deep: true })
watch(() => props.quoteEnabled, buildQuote)

onBeforeUnmount(cancelQuote)
</script>
